import { actionUpdate, updateNodeData } from '@stackhouseos/flower-client'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import { getWarehouseItems, putWarehouseItems } from '../../api/iPratico'
import { Order } from '../orders/model'
import {
  makeSelectOrderProductsByOrderId,
  selectCurrentOrderWithStore
} from '../orders/selectors'
import { OrderProduct } from '../ordersProducts/model'
import { Product } from '../products/model'
import { makeSelectProductById } from '../products/selectors'
import { Store } from '../stores/model'

export const startIpraticoSaga = () => ({
  type: 'START_IPRATICO_SAGA'
})

function* getProductByIdGenerator(id: string) {
  const product: Product = yield select(makeSelectProductById(id))
  return product
}

// Funzione per trovare il prodotto nel magazzino
function findWarehouseProduct(
  product: Product,
  matchWarehouseProducts: Record<string, any>[]
) {
  return matchWarehouseProducts.find((p) =>
    p.product.value.productCodes.some(
      (pp: Record<string, any>) => pp.codeType === 'SKU' && pp.code === product.sku
    )
  )
}

function* updateIpraticoQtySaga({ payload }: any) {
  const { flowName } = payload || {}

  try {
    const order: Order & { deliveryStore: Store } = yield select(
      selectCurrentOrderWithStore
    )
    const orderID = order?._id
    const store = order?.deliveryStore || order?.store
    const store_apikey = store?.iPratico

    if (!store_apikey) return new Error('Non è stato possibile connettersi al magazzino')
    const orderProducts: OrderProduct[] = yield select(
      makeSelectOrderProductsByOrderId(orderID)
    )
    // mix Product with OrderProduct.quantity so we have sku and quantity in the same place
    const products: Product[] = yield all(
      orderProducts.map((p) =>
        call(function* () {
          const product: Product = yield call(getProductByIdGenerator, p.productId)
          return { ...product, quantity: p.quantity }
        })
      )
    )
    // get all warehouse items from iPratico api
    const { data: AllWarehouseProducts } = yield call(getWarehouseItems, store_apikey)

    // filter warehouse items from iPratico to match our products in order
    const matchWarehouseProducts: Record<string, any>[] = AllWarehouseProducts.filter(
      (p: Record<string, any>) => {
        const onlyLocalSkus = products.map((p) => p.sku)
        const currentRemoteSku =
          p?.product?.value?.productCodes?.find(
            (p: Record<string, any>) => p.codeType === 'SKU'
          )?.code || null
        return onlyLocalSkus.includes(currentRemoteSku)
      }
    )

    // Creazione della lista dei match
    const matchedProducts = products.map((product) => {
      const warehouseProduct = findWarehouseProduct(product, matchWarehouseProducts)
      if (!warehouseProduct) {
        throw new Error(
          `Prodotto ${product.title} SKU: ${product.sku} non trovato nel magazzino ${store?.name}.`
        )
      }
      return { product, warehouseProduct }
    })

    // Creazione delle richieste di aggiornamento
    const updateProductRequests = matchedProducts.map(({ product, warehouseProduct }) => {
      const updateQuantity =
        warehouseProduct.value.availableStockQuantity + product.quantity

      return call(putWarehouseItems, {
        id: warehouseProduct.id,
        body: {
          productId: warehouseProduct.value.productId,
          cas: warehouseProduct.cas,
          availableStockQuantity: updateQuantity
        },
        apiKey: store_apikey
      })
    })

    // @ts-ignore
    const results: any = yield all(updateProductRequests)
  } catch (err: unknown) {
    const errorMsg =
      err instanceof Error
        ? err?.message
        : 'Non è stato possibile procedere con la richiesta'
    if (flowName) {
      yield put(actionUpdate('flower', `${flowName}.data.error`, errorMsg))
      yield put(updateNodeData(flowName, 'error', errorMsg))
    }
  } finally {
    if (flowName) yield put({ type: 'flower/next', payload: { name: flowName } })
  }
}

export function* iPraticoSaga() {
  yield takeLatest(startIpraticoSaga().type, updateIpraticoQtySaga)
}
